import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import {
  hasUserAccess,
  getUserDetails,
  getUserRole,
} from "../../../../backend/auth";
import {
  useGetVendorHeirarchyRecords
} from "../../../../backend/masters";

const INCLUDE_USER_COMPANY_ASSOCIATIONS = {
  parent_company: {
    associate_companies: {
      company_location_mappings: {
        location: ['city']
      }
    }
  }
};

const ALL_OPTION = {
  value: "all",
  label: "ALL"
};

function extractCategories(companiesList: any[]) {
  const map = new Map<any, any>();
  (companiesList || []).forEach((c: any) => {
    (c.associate_companies || []).forEach((ac: any) => {
      (ac.locations || []).forEach((loc: any) => {
        (loc.categories || []).forEach((cat: any) => {
          if (!map.has(cat.id)) {
            map.set(cat.id, { ...cat, vendors: [...(cat.vendors || [])] });
          } else {
            const existing = map.get(cat.id);
            const existingIds = new Set(existing.vendors.map((v: any) => v.id));
            (cat.vendors || []).forEach((v: any) => {
              if (!existingIds.has(v.id)) { existing.vendors.push(v); existingIds.add(v.id); }
            });
          }
        });
      });
    });
  });
  return Array.from(map.values());
}

function extractVendors(categoriesList: any[]) {
  const seen = new Set();
  const result: any[] = [];
  (categoriesList || []).forEach((cat: any) => {
    (cat.vendors || []).forEach((v: any) => {
      if (!seen.has(v.id)) { seen.add(v.id); result.push(v); }
    });
  });
  return result;
}

function AdminVendorLocations({ onChange, previousFilters }: any) {
  const _role = getUserRole();
  const [t] = useState(new Date().getTime());
  const [associateCompanies, setAssociateCompanies] = useState<any>(null);
  const [locations, setLocations] = useState<any>(null);
  const [categories, setCategories] = useState<any>(null);
  const [vendors, setVendors] = useState<any>(null);

  const [company, setCompany] = useState<any>(ALL_OPTION);
  const [associateCompany, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);
  const [category, setCategory] = useState<any>(ALL_OPTION);
  const [vendor, setVendor] = useState<any>(ALL_OPTION);
  const initialLoadComplete = useRef(false);
  const user = getUserDetails();

  const { companies, isFetching }: any = useGetVendorHeirarchyRecords({
    filters: {
      user_id_eq: user.user_id,
      is_active_eq: true
    },
    // includes: INCLUDE_USER_COMPANY_ASSOCIATIONS
  });

  useEffect(() => {
    setAssociateCompany(ALL_OPTION);
    setLocations(null);
    setLocation(ALL_OPTION);
    setCategory(ALL_OPTION);
    setVendor(ALL_OPTION);

    if (company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      if (_company) {
        setAssociateCompanies(_company.associate_companies);
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else {
      setAssociateCompanies(null);
      const cats = extractCategories(companies);
      setCategories(cats);
      setVendors(extractVendors(cats));
    }
  }, [company]);

  useEffect(() => {
    setLocations(null);
    setLocation(ALL_OPTION);
    setCategory(ALL_OPTION);
    setVendor(ALL_OPTION);

    if (associateCompany && associateCompany.value !== "all" && company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      const _ac = _company.associate_companies.find((c: any) => c.id == associateCompany.value);
      if (_ac) {
        setLocations(_ac.locations);
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else if (company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      if (_company) {
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    }
  }, [associateCompany]);

  useEffect(() => {
    setCategory(ALL_OPTION);
    setVendor(ALL_OPTION);

    if (location && location.value !== "all" && associateCompany && associateCompany.value !== "all" && company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies.find((c: any) => c.id == associateCompany.value);
      const _loc = _ac && _ac.locations.find((c: any) => c.id == location.value);
      if (_loc) {
        const cats = extractCategories([{ associate_companies: [{ locations: [_loc] }] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else if (associateCompany && associateCompany.value !== "all" && company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies.find((c: any) => c.id == associateCompany.value);
      if (_ac) {
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    }
  }, [location]);

  useEffect(() => {
    setVendor(ALL_OPTION);

    if (category && category.value !== "all") {
      const _cat = (categories || []).find((c: any) => c.id == category.value);
      if (_cat) {
        setVendors(_cat.vendors || []);
      }
    } else {
      setVendors(extractVendors(categories || []));
    }
  }, [category]);

  useEffect(() => {
    if (companies && companies.length > 0 && !isFetching) {
      const cats = extractCategories(companies);
      setCategories(cats);
      setVendors(extractVendors(cats));

      if (Object.keys(previousFilters).length) {
        const _company = companies.find((as: any) => as.id == (previousFilters.company || {}).value);
        if (_company) {
          setCompany({ value: _company.id, label: _company.name });
        }
      }
    }
  }, [companies])

  useEffect(() => {
    if (initialLoadComplete.current) return;
    if (associateCompanies && associateCompanies.length > 0 && Object.keys(previousFilters).length) {
      if (company && company.value === (previousFilters.company || {}).value) {
        const _company = associateCompanies.find((as: any) => as.id == (previousFilters.associateCompany || {}).value);
        if (_company) {
          setAssociateCompany({ value: _company.id, label: _company.name });
        }
      }
    }
  }, [associateCompanies])

  useEffect(() => {
    if (initialLoadComplete.current) return;
    if (locations && locations.length > 0 && Object.keys(previousFilters).length) {
      if (company && company.value === (previousFilters.company || {}).value &&
        associateCompany && associateCompany.value === (previousFilters.associateCompany || {}).value) {
        const _location = locations.find((as: any) => as.id == ((previousFilters.locations || [])[0] || {}).value);
        if (_location) {
          setLocation({ value: _location.id, label: _location.name });
        }
      }
    }
  }, [locations])

  useEffect(() => {
    if (initialLoadComplete.current) return;
    if (categories && categories.length > 0 && Object.keys(previousFilters).length) {
      if (company && company.value === (previousFilters.company || {}).value &&
        associateCompany && associateCompany.value === (previousFilters.associateCompany || {}).value) {
        const _category = categories.find((as: any) => as.id == ((previousFilters.vendorCategories || [])[0] || {}).value);
        if (_category) {
          setCategory({ value: _category.id, label: _category.name });
        }
      }
    }
  },[categories])

  useEffect(() => {
    if (initialLoadComplete.current) return;
    if (vendors && vendors.length > 0 && Object.keys(previousFilters).length) {
      if (company && company.value === (previousFilters.company || {}).value &&
        associateCompany && associateCompany.value === (previousFilters.associateCompany || {}).value &&
        category && category.value === ((previousFilters.vendorCategories || [])[0] || {}).value) {
        const _vendor = vendors.find((as: any) => as.id == ((previousFilters.vendorRegistrations || [])[0] || {}).value);
        if (_vendor) {
          setVendor({ value: _vendor.id, label: _vendor.name });
        }
        initialLoadComplete.current = true;
      }
    }
    if (!Object.keys(previousFilters).length && vendors && vendors.length > 0) {
      initialLoadComplete.current = true;
    }
  },[vendors])

  useEffect(() => {
    // Don't call onChange until companies are loaded
    if (!companies || companies.length === 0) {
      return;
    }

    const filters: any = {};
    const filterData: any = {};

    if (company && company.value === "all") {
      const allCompanyIds = (companies || []).map((c: any) => c.id);
      if (allCompanyIds.length > 0) {
        filters.company_id_in = allCompanyIds;
      }
    } else if (company && company.value !== "all") {
      filters.company_id_eq = company.value;
      filterData.company = company;
    }

    if (associateCompany && associateCompany.value !== "all") {
      filters.associate_company_id_eq = associateCompany.value;
      filterData.associateCompany = associateCompany;
    }

    if (location && location.value !== "all") {
      filters.location_id_eq = location.value;
      filterData.locations = [location];
    }

    if (category && category.value !== "all") {
      filters.vendor_category_id_eq = category.value;
      filterData.vendorCategories = [category];
    }

    if (vendor && vendor.value !== "all") {
      filters.vendor_registration_id_eq = vendor.value;
      filterData.vendorRegistrations = [vendor];
    }

    onChange(filters, filterData);
  }, [company, associateCompany, location, category, vendor, companies]);


  function selectVendor(selected: any) {
    if(selected.value === vendor.value) {
      return
    } else {
      setVendor(selected);
    }
  }

  return (
    <>
      <div className="px-2">
        <label className="filter-label">
          <small>Company</small>
        </label>
        <Select
          placeholder="Company"
          options={[
            ALL_OPTION,
            ...(companies || []).map((x: any) => {
              return { value: x.id, label: x.name };
            })
          ]}
          onChange={setCompany}
          value={company}
          className="select-control"
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Associate Company</small>
        </label>
        <Select
          placeholder="Associate Company"
          options={
            Array.isArray(associateCompanies)
              ? [
                  ALL_OPTION,
                  ...associateCompanies.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setAssociateCompany}
          value={associateCompany}
          className="select-control"
          isDisabled={!company || company.value === "all"}
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Location</small>
        </label>
        <Select
          placeholder="Location"
          options={
            Array.isArray(locations)
              ? [
                  ALL_OPTION,
                  ...locations.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setLocation}
          value={location}
          className="select-control"
          isDisabled={!associateCompany || associateCompany.value === "all"}
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Vendor Category</small>
        </label>
        <Select
          placeholder="Category"
          options={
            Array.isArray(categories)
              ? [
                  ALL_OPTION,
                  ...categories.map((x: any) => {
                    return { value: x.id, label: x.name };
                  })
                ]
              : [ALL_OPTION]
          }
          onChange={setCategory}
          value={category}
          className="select-control"
          // isDisabled={!location || location.value === "all"}
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Vendor</small>
        </label>
        <Select
          placeholder="Vendors"
          options={
            Array.isArray(vendors)
              ? [
                  ALL_OPTION,
                  ...vendors.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={selectVendor}
          value={vendor}
          className="select-control"
          // isDisabled={!category || category.value === "all"}
        />
      </div>
    </>
  );
}

export default AdminVendorLocations;
